import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import "./Tree.css"

/**
 * TangledTree
 * -----------
 * A vertical "tangled tree" DAG visualization in the spirit of
 * https://observablehq.com/@nitaku/tangled-tree-visualization-ii
 *
 * Layout:
 *  - Nodes are grouped into rows ("levels") based on how deep they sit in
 *    the dependency graph (root nodes at the top, growing downward).
 *  - Each node renders as a small circle with its label in a line to the
 *    right (not centered inside a shape) — a subway-map / progress-trail
 *    look rather than a flowchart-pill look.
 *  - The whole diagram is a single responsive SVG that scales to the
 *    container's width (no horizontal scrolling, ever) and grows as tall
 *    as it needs to — the container scrolls vertically when content
 *    exceeds the viewport, like a normal mobile page.
 *  - No pan/zoom gestures: this is designed to feel like part of the page,
 *    not a canvas you drag around.
 *
 * Node states:
 *  - unfinished (not marked read): an open circle — transparent fill,
 *    colored stroke.
 *  - answered (marked read): the circle is filled solid.
 *  - "ready" (unfinished, but with no unread prerequisites — i.e. it's
 *    unlocked and doable right now): gets an extra, larger halo ring
 *    drawn around the dot so it stands out as the thing to do next.
 *
 * Color model (read this if you're theming):
 *  - Every property that depends on markedRead (dot fill, dot stroke,
 *    halo visibility, glow filter, label color) is set in ONE place:
 *    setNodeAppearance(). It's called both by the initial draw effect and
 *    by the color-only effect that runs on every markedRead toggle, so
 *    there's a single source of truth and toggling can never silently do
 *    nothing.
 *  - CSS variables, with fallbacks so it still looks fine unset:
 *      --node-fill-unfinished / --node-fill-read  (dot fill)
 *      --node-border                               (dot stroke, same both states)
 *      --node-halo                                 (ready-state ring color)
 *      --node-text / --node-text-blocked          (label color)
 *      --node-group-title                          (group title heading color)
 *      --tt-glow-unread   / --tt-glow-read        (glow color)
 *      --tt-line                                   (connector line color, solid mode)
 *      --tt-line-1 .. --tt-line-5                  (connector palette, bundle mode)
 */

// ── Public data shape ─────────────────────────────────────────────────────

export type TangleNode = {
  id: string;
  title: string;
  text?: string;
  /** ids of nodes this node directly depends on (its parents / prerequisites) */
  parents: string[];
  markedRead: boolean;
  /** Set only on a node group's first problem — rendered as a small heading
   *  to its left instead of as a node of its own. */
  groupTitle?: string;
};

type InternalNode = TangleNode & {
  level: number;
  order: number;
  x: number;
  y: number;
  w: number;
  h: number;
  lines: string[];
  /** Wrapped, already-uppercased group title lines, last line closest to the node. */
  groupTitleLines: string[];
  bundleId: string | null;
};

type InternalLink = {
  source: InternalNode;
  target: InternalNode;
  bundleId: string;
  /** where this line leaves the bottom edge of its source, relative to source.x */
  sourceOffsetX: number;
  /** where this line arrives at the top edge of its target, relative to target.x */
  targetOffsetX: number;
  /** absolute y where this line makes its horizontal run, just past halfway
   *  through the gap immediately above its target's row */
  turnY: number;
};

const ROW_SPACING = 120; // vertical distance between row centers
const NODE_RADIUS = 9; // the circle itself
const HALO_RADIUS = 18; // "ready" ring drawn around the circle
const NODE_STROKE_WIDTH = 3;
const HALO_STROKE_WIDTH = 2.5;
const NODE_MIN_H = 44; // >=44px keeps tap targets comfortable on mobile
const LABEL_GAP = 18; // gap between the circle's edge and the start of its label
const NODE_GAP = 80; // horizontal gap between node+label groups in the same row
const MARGIN_X = 20;
const MARGIN_Y = 40;
const GROUP_TITLE_GAP = 18; // gap between a group title's right edge and the circle's left edge
const GROUP_TITLE_FONT_SIZE = 12;
const GROUP_TITLE_LINE_HEIGHT = 14;
const MAX_GROUP_TITLE_WIDTH = 160; // wrap the heading instead of letting it run wide
const FONT_SIZE = 16;
const LINE_GAP = 8; // fixed spacing between parallel lines, so they read as distinct tracks rather than merging
const TURN_FRACTION = 0.6; // "just past halfway" through the final gap before a target
const CORNER_RADIUS = 8;
const MAX_LABEL_WIDTH = 450; // wrap text instead of growing labels past this
const LINE_HEIGHT = 15;

const BUNDLE_PALETTE = [
  "var(--tt-line-1, #6366f1)",
  "var(--tt-line-2, #22c55e)",
  "var(--tt-line-3, #f97316)",
  "var(--tt-line-4, #ec4899)",
  "var(--tt-line-5, #14b8a6)",
];

// Every property that depends on markedRead lives here, and only here.
// Both the initial draw effect and the toggle-driven color-only effect
// call this, so there's one source of truth and no way for the two to
// drift out of sync (which is what was causing "mark as read" to look
// like it did nothing).
function setNodeAppearance<T extends { id: string }>(
    selection: d3.Selection<SVGGElement, T, SVGGElement, unknown>,
    statusOf: (d: T) => ReadStatus
) {
  // Dot: open (transparent, colored stroke) until answered, then filled solid.
  const fillFor = (s: ReadStatus) => (s === "read" ? "var(--node-fill-read, #153761)" : "var(--node-fill-unfinished, transparent)");
  const borderColor = "var(--node-border, #153761)"; // same stroke color in every unfinished/ready state

  // Halo: an extra ring around the dot, shown only when the node is
  // unlocked (no unread prereqs) and not yet answered — i.e. "do this next".
  const haloVisible = (s: ReadStatus) => s === "ready";

  const textFor = (s: ReadStatus) => (s === "blocked" ? "var(--node-text-blocked, #6e645c)" : "var(--node-text, #201f1d)");

  selection
      .select<SVGCircleElement>("circle.tt-node-dot")
      .attr("fill", (d) => fillFor(statusOf(d)))
      .attr("stroke", borderColor);
      // Shadows disabled — re-enable by restoring this and the filter defs above:
      // .attr("filter", (d) => `url(#${statusOf(d) === "read" ? "tt-read-glow" : "tt-unread-glow"})`);

  selection
      .select<SVGCircleElement>("circle.tt-node-halo")
      .style("opacity", (d) => (haloVisible(statusOf(d)) ? 1 : 0))
      .attr("stroke", "var(--node-halo, #153761)");

  selection.select("text").style("fill", (d) => textFor(statusOf(d)));
}

export default function TangledTree({
                                      nodes,
                                      onOpenNode,
                                      onToggleRead,
                                      lineColor,
                                      colorLinesByBundle = false,
                                      beforeContent,
                                      afterContent,
                                    }: {
  nodes: TangleNode[];
  onOpenNode?: (id: string) => void;
  onToggleRead?: (id: string) => void;
  /** Solid color for every connector line. Defaults to the --tt-line CSS var. Ignored if colorLinesByBundle is true. */
  lineColor?: string;
  /** Opt into coloring lines by shared-parent-set "bundle" instead of one solid color. */
  colorLinesByBundle?: boolean;
  /** Rendered inside the tree's own scrollable content, directly above/
   *  below the node graph — so it scrolls away with the tree instead of
   *  staying pinned outside it. */
  beforeContent?: React.ReactNode;
  afterContent?: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const nodeSelectionRef = useRef<d3.Selection<SVGGElement, InternalNode, SVGGElement, unknown> | null>(null);
  // Always-current id -> node lookup, kept fresh independent of the
  // structure-only draw effect, so the click handler (bound once per
  // structural layout) can still read up-to-date markedRead state.
  const nodesByIdRef = useRef<Map<string, TangleNode>>(new Map());

  const MOBILE_QUERY = "(max-width: 500px)";

  function useIsMobile() {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
    );

    useEffect(() => {
      const mql = window.matchMedia(MOBILE_QUERY);
      const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }, []);

    return isMobile;
  }

  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    id: string;
    title: string;
    text?: string;
    markedRead: boolean;
    unreadPrereqs: string[];
  } | null>(null);

  const [menuCenterX, setMenuCenterX] = useState<number | null>(null);

  useEffect(() => {
    if (!menu || !containerRef.current) return;

    function updateCenter() {
      const rect = containerRef.current!.getBoundingClientRect();
      setMenuCenterX(rect.left + rect.width / 2);
    }

    updateCenter();
    window.addEventListener("resize", updateCenter);
    return () => window.removeEventListener("resize", updateCenter);
  }, [menu]);

  // Dismiss the menu on a tap/click anywhere in the document — not just
  // inside this component's own box. This is deliberately global (rather
  // than an onClick on our container) so it keeps working no matter how
  // this component ends up sized or nested by the page around it.
  useEffect(() => {
    if (!menu) return;

    function handlePointerDown(event: PointerEvent) {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) {
        // Let clicks on the menu's own buttons behave normally.
        return;
      }
      setMenu(null);
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [menu]);

  // Stable key: only structural changes (nodes/edges added or removed)
  // should trigger a re-layout. Toggling markedRead should not.
  const structureKey = useMemo(
      () => nodes.map((n) => `${n.id}:${n.parents.join(",")}:${n.title}`).join("|"),
      [nodes]
  );

  const {internalNodes, internalLinks, width, height} = useMemo(
      () => layoutTangledTree(nodes),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [structureKey]
  );

  // ── DRAW EFFECT (structure only) ──────────────────────────────────────
  useEffect(() => {
    if (!svgRef.current) return;
    if (internalNodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    // Render at true 1:1 pixel size — no viewBox scaling — so node/label
    // sizes stay constant no matter how many columns a given tree has.
    // Narrower-than-container trees get centered (see .tt-content svg in
    // Tree.css); wider ones scroll horizontally instead of shrinking.
    svg.attr("width", width).attr("height", height);

    const root = svg.append("g");

    // ── glow filters (disabled — shadows commented out for now). Give
    // unread/read genuinely different colors here (not the same var twice)
    // if you want the glow itself to carry meaning; otherwise it's just a
    // soft shadow either way.
    // svg
    //     .append("filter")
    //     .attr("id", "tt-unread-glow")
    //     .attr("x", "-50%")
    //     .attr("y", "-50%")
    //     .attr("width", "300%")
    //     .attr("height", "300%")
    //     .append("feDropShadow")
    //     .attr("dx", 0)
    //     .attr("dy", 0)
    //     .attr("stdDeviation", 3)
    //     .attr("flood-color", "var(--tt-glow-unread, #6366f1)")
    //     .attr("flood-opacity", 0.6);
    //
    // svg
    //     .append("filter")
    //     .attr("id", "tt-read-glow")
    //     .attr("x", "-50%")
    //     .attr("y", "-50%")
    //     .attr("width", "300%")
    //     .attr("height", "300%")
    //     .append("feDropShadow")
    //     .attr("dx", 0)
    //     .attr("dy", 0)
    //     .attr("stdDeviation", 3)
    //     .attr("flood-color", "var(--tt-glow-read, #22c55e)")
    //     .attr("flood-opacity", 0.6);

    // ── links, drawn as orthogonal (subway-map style) connectors ─────────
    const linkGroup = root.append("g").attr("fill", "none");

    const bundleIds = Array.from(new Set(internalLinks.map((d) => d.bundleId)));
    const colorForBundle = d3.scaleOrdinal<string, string>().domain(bundleIds).range(BUNDLE_PALETTE);

    linkGroup
        .selectAll("path")
        .data(internalLinks)
        .enter()
        .append("path")
        .attr("stroke", (d) =>
            colorLinesByBundle ? colorForBundle(d.bundleId) : lineColor ?? "var(--tt-line, #94a3b8)"
        )
        .attr("stroke-opacity", 0.7)
        .attr("stroke-width", 2.5)
        .attr("d", (d) => tangleLinkPath(d));

    // ── nodes: a small circle with its label in a line to the right ──────
    const node = root
        .append("g")
        .selectAll<SVGGElement, InternalNode>("g")
        .data(internalNodes)
        .enter()
        .append("g")
        .attr("data-node-id", (d) => d.id)
        .attr("transform", (d) => `translate(${d.x},${d.y})`)
        .style("cursor", "pointer");

    // Ready-state halo, drawn behind the dot. Visibility is toggled in
    // setNodeAppearance, not here, so it stays in sync with markedRead.
    node
        .append("circle")
        .attr("class", "tt-node-halo")
        .attr("r", HALO_RADIUS)
        .attr("fill", "none")
        .attr("stroke-width", HALO_STROKE_WIDTH);

    node
        .append("circle")
        .attr("class", "tt-node-dot")
        .attr("r", NODE_RADIUS)
        .attr("stroke-width", NODE_STROKE_WIDTH);

    node
        .append("text")
        .attr("text-anchor", "start")
        .attr("dominant-baseline", "central")
        .style("font-size", `${FONT_SIZE}px`)
        .style("pointer-events", "none")
        .each(function (d) {
          const text = d3.select(this);
          const offset = ((d.lines.length - 1) * LINE_HEIGHT) / 2;
          d.lines.forEach((line, i) => {
            text
                .append("tspan")
                .attr("x", NODE_RADIUS + LABEL_GAP)
                .attr("y", i * LINE_HEIGHT - offset + 4)
                .text(line);
          });
        });

    // A node group's title, shown as a small (wrapped, max-width) heading
    // to the left of its first problem node instead of as a node/circle of
    // its own. Right-aligned and vertically centered on the node, like a
    // section label in the margin.
    node
        .filter((d) => d.groupTitleLines.length > 0)
        .append("text")
        .attr("class", "tt-group-title")
        .attr("text-anchor", "end")
        .attr("dominant-baseline", "central")
        .style("font-family", "var(--font-mono)")
        .style("font-size", `${GROUP_TITLE_FONT_SIZE}px`)
        .style("font-weight", 700)
        .style("letter-spacing", "0.08em")
        .style("fill", "var(--node-group-title, var(--text-on-paper-soft, #6e645c))")
        .style("pointer-events", "none")
        .each(function (d) {
          const text = d3.select(this);
          const x = -(NODE_RADIUS + GROUP_TITLE_GAP);
          const offset = ((d.groupTitleLines.length - 1) * GROUP_TITLE_LINE_HEIGHT) / 2;
          d.groupTitleLines.forEach((line, i) => {
            text
                .append("tspan")
                .attr("x", x)
                .attr("y", i * GROUP_TITLE_LINE_HEIGHT - offset + 4)
                .text(line);
          });
        });

    // Apply the actual read/unread colors — same helper the toggle effect uses.
    const nodeMap = new Map(internalNodes.map((n) => [n.id, n]));
    setNodeAppearance(node, (d) => nodeStatus(d, nodeMap));

    // Larger, invisible hit-area (covering both the dot and its label) so
    // small circles are still easy to tap.
    node
        .append("rect")
        .attr("x", -NODE_RADIUS - 8)
        .attr("y", (d) => -d.h / 2 - 10)
        .attr("width", (d) => d.w + 16)
        .attr("height", (d) => d.h + 20)
        .attr("fill", "transparent");

    node.on("click", function (this: SVGGElement, event, d) {
      event.stopPropagation();
      const rect = this.getBoundingClientRect();
      // Compute the menu's centered x position up front, in the same tick
      // as opening it, so it appears already in place instead of painting
      // at its unstyled (left-edge) position first and snapping over once
      // the effect below runs a frame later.
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        setMenuCenterX(containerRect.left + containerRect.width / 2);
      }
      setMenu({
        x: rect.left + rect.width / 2,
        y: rect.bottom,
        id: d.id,
        title: d.title,
        text: d.text,
        markedRead: nodesByIdRef.current.get(d.id)?.markedRead ?? d.markedRead,
        unreadPrereqs: getUnreadPrerequisites(d.id, nodesByIdRef.current),
      });
    });

    nodeSelectionRef.current = node;

    // Land on the problem the user's actually up to instead of the top of
    // the tree — the "ready" node (unfinished, no unread prerequisites) is
    // exactly that: the next thing to do. In this linear-chain layout
    // there's normally at most one. Runs off the same structural effect as
    // the rest of the draw (mount, or switching to a chapter with a
    // different node set) rather than the color-only toggle effect, so
    // marking a node read elsewhere never yanks the scroll position out
    // from under the user.
    const currentNode = internalNodes.find((d) => nodeStatus(d, nodeMap) === "ready");

    if (currentNode && containerRef.current) {
      const container = containerRef.current;
      const el = container.querySelector<SVGGElement>(`[data-node-id="${CSS.escape(currentNode.id)}"]`);

      if (el) {
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const offsetTop = elRect.top - containerRect.top + container.scrollTop;
        const offsetLeft = elRect.left - containerRect.left + container.scrollLeft;

        container.scrollTo({
          top: offsetTop - container.clientHeight / 2,
          left: offsetLeft - container.clientWidth / 2,
          behavior: "auto",
        });
      }
    }

    return () => {
      nodeSelectionRef.current = null;
    };
  }, [internalNodes, internalLinks, width, height, lineColor, colorLinesByBundle]);

  // ── COLOR-ONLY EFFECT (markedRead toggles, no re-layout) ──────────────
  useEffect(() => {
    nodesByIdRef.current = new Map(nodes.map((n) => [n.id, n]));

    if (!nodeSelectionRef.current) return;
    const readMap = new Map(nodes.map((n) => [n.id, n.markedRead]));

    const byId = new Map(nodes.map((n) => [n.id, n]));
    setNodeAppearance(nodeSelectionRef.current, (d) => nodeStatus(byId.get(d.id) ?? d, byId));
  }, [nodes]);

  const isMobile = useIsMobile();

  return (
      <div
          ref={containerRef}
          className="tt-scroll-container"
          style={{
            width: "100%",
            height: "100%",
            overflowY: "auto",
            // Nodes render at a fixed pixel size regardless of column count
            // (see the draw effect), so a wide row scrolls horizontally
            // instead of shrinking everything to fit.
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            position: "relative",
            touchAction: "pan-x pan-y",
          }}
      >
        {/* Fill the screen (100vh with a 100dvh upgrade for mobile browser chrome),
         and hide the scrollbar while keeping the container scrollable. */}
        <style>{`.tt-scroll-container { height: 100%; scrollbar-width: none; -ms-overflow-style: none; }
.tt-scroll-container::-webkit-scrollbar { display: none; width: 0; height: 0; }`}</style>

        <div className="tt-scroll-container">
          <div className="tt-content">
            {beforeContent}
            <svg ref={svgRef}/>
            {afterContent}
          </div>
        </div>

        {menu && (
            <div
                ref={menuRef}
                className="tt-menu"
                style={menuCenterX !== null ? {left: menuCenterX} : undefined}
            >
              <p className="tt-menu__title">{menu.title}</p>
              {menu.text && <p className="tt-menu__text">{menu.text}</p>}

              {/*<hr className="tt-menu__divider" />*/}

              <div className="tt-menu__actions">
                <button disabled={menu?.unreadPrereqs.length !== 0} className="tt-menu__button tt-menu__button--primary"
                        onClick={() => {
                          if (menu?.unreadPrereqs.length === 0) {
                            onOpenNode?.(menu.id);
                            setMenu(null);
                          }
                        }}>
                  {menu.unreadPrereqs.length > 0 ? "Complete previous problem" : "Open"}
                </button>
              </div>
            </div>
        )}
      </div>
  );
}

// ── Layout algorithm ────────────────────────────────────────────────────

type ReadStatus = "read" | "ready" | "blocked";

function nodeStatus(node: TangleNode, byId: Map<string, TangleNode>): ReadStatus {
  if (node.markedRead) return "read";
  const hasUnreadParent = node.parents.some((pid) => byId.get(pid)?.markedRead === false);
  return hasUnreadParent ? "blocked" : "ready";
}

// Direct parents of `nodeId` that haven't been marked read yet — surfaced
// in the click menu so a person can see what's blocking a topic at a
// glance, ported from the reference Graph.tsx's getUnreadPrerequisites.
function getUnreadPrerequisites(nodeId: string, byId: Map<string, TangleNode>): string[] {
  const node = byId.get(nodeId);
  if (!node) return [];
  return node.parents
      .map((pid) => byId.get(pid))
      .filter((p): p is TangleNode => !!p && !p.markedRead)
      .map((p) => p.title);
}

function measureTextWidth(text: string, fontSize: number, fontFamily: string = "sans-serif"): number {
  if (typeof document === "undefined") return text.length * fontSize * 0.6;
  const canvas = measureTextWidth as unknown as { _canvas?: HTMLCanvasElement };
  if (!canvas._canvas) canvas._canvas = document.createElement("canvas");
  const ctx = canvas._canvas.getContext("2d");
  if (!ctx) return text.length * fontSize * 0.6;
  ctx.font = `${fontSize}px ${fontFamily}`;
  return ctx.measureText(text).width;
}

// Rough width of a group title heading as actually rendered: uppercase,
// monospace-ish, with letter-spacing added between characters (which
// ctx.measureText alone doesn't account for).
function measureGroupTitleWidth(title: string): number {
  const upper = title.toUpperCase();
  const letterSpacing = GROUP_TITLE_FONT_SIZE * 0.08;
  return measureTextWidth(upper, GROUP_TITLE_FONT_SIZE, "monospace") + Math.max(0, upper.length - 1) * letterSpacing;
}

// Wraps a group title to maxWidth, word by word, the same way node labels
// wrap — returns already-uppercased lines ready to render.
function wrapGroupTitle(title: string, maxWidth: number): string[] {
  const words = title.split(/\s+/);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (measureGroupTitleWidth(candidate) > maxWidth && current) {
      lines.push(current.toUpperCase());
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current.toUpperCase());
  return lines;
}

function wrapTitle(title: string, fontSize: number, maxTextWidth: number): string[] {
  const words = title.split(/\s+/);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (measureTextWidth(candidate, fontSize) > maxTextWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function layoutTangledTree(input: TangleNode[]): {
  internalNodes: InternalNode[];
  internalLinks: InternalLink[];
  width: number;
  height: number;
} {
  if (input.length === 0) {
    return { internalNodes: [], internalLinks: [], width: 0, height: 0 };
  }

  const byId = new Map(input.map((n) => [n.id, n]));

  // 1. Assign each node a level = 1 + max(level of parents), via memoized
  // recursion. Nodes with no parents (or missing/unknown parents) sit at 0.
  const levelCache = new Map<string, number>();
  const visiting = new Set<string>();

  function levelOf(id: string): number {
    if (levelCache.has(id)) return levelCache.get(id)!;
    const n = byId.get(id);
    if (!n || n.parents.length === 0) {
      levelCache.set(id, 0);
      return 0;
    }
    if (visiting.has(id)) {
      // cycle guard — treat as a root to avoid infinite recursion
      levelCache.set(id, 0);
      return 0;
    }
    visiting.add(id);
    const knownParents = n.parents.filter((p) => byId.has(p));
    const lvl = knownParents.length === 0 ? 0 : 1 + Math.max(...knownParents.map(levelOf));
    visiting.delete(id);
    levelCache.set(id, lvl);
    return lvl;
  }

  input.forEach((n) => levelOf(n.id));

  const maxLevel = Math.max(...input.map((n) => levelCache.get(n.id)!));
  const columns: TangleNode[][] = Array.from({ length: maxLevel + 1 }, () => []);
  input.forEach((n) => columns[levelCache.get(n.id)!].push(n));

  // 2. Order within each row using a barycenter heuristic against the
  // previous row's order, iterated a few times to reduce crossings.
  const orderIndex = new Map<string, number>();
  columns[0].forEach((n, i) => orderIndex.set(n.id, i));

  for (let pass = 0; pass < 4; pass++) {
    for (let lvl = 1; lvl <= maxLevel; lvl++) {
      const row = columns[lvl];
      const scored = row.map((n) => {
        const knownParents = n.parents.filter((p) => byId.has(p) && orderIndex.has(p));
        const bary =
            knownParents.length === 0
                ? orderIndex.get(n.id) ?? 0
                : d3.mean(knownParents, (p) => orderIndex.get(p)!)!;
        return { n, bary };
      });
      scored.sort((a, b) => a.bary - b.bary);
      scored.forEach((s, i) => orderIndex.set(s.n.id, i));
      columns[lvl] = scored.map((s) => s.n);
    }
  }

  // 3. Size each node's label. A node's "box" is its circle plus the label
  // running to the right of it; node.x (used for the circle and for
  // connectors) is the circle's center, at the left edge of that box.
  const nodeSize = new Map<string, { w: number; h: number; lines: string[]; groupTitleLines: string[] }>();
  input.forEach((n) => {
    const singleLineWidth = measureTextWidth(n.title, FONT_SIZE);
    const lines = singleLineWidth <= MAX_LABEL_WIDTH ? [n.title] : wrapTitle(n.title, FONT_SIZE, MAX_LABEL_WIDTH);
    const textWidth = Math.min(MAX_LABEL_WIDTH, Math.max(...lines.map((line) => measureTextWidth(line, FONT_SIZE))));

    // A group title now sits to the *left* of the circle instead of inside
    // the node's own rightward box, so it doesn't affect `w` at all — just
    // the shared left margin, computed separately below.
    const groupTitleLines = n.groupTitle ? wrapGroupTitle(n.groupTitle, MAX_GROUP_TITLE_WIDTH) : [];

    const w = NODE_RADIUS * 2 + LABEL_GAP + textWidth;
    const h = Math.max(NODE_MIN_H, lines.length * LINE_HEIGHT + 20);
    nodeSize.set(n.id, { w, h, lines, groupTitleLines });
  });

  // How far group titles reach left of the leftmost lane's circle — has to
  // fit the widest title line in the whole tree, or a long one would run
  // off the edge of the canvas.
  const maxGroupTitleWidth = Math.max(
      0,
      ...input.flatMap((n) => nodeSize.get(n.id)!.groupTitleLines.map((line) => measureGroupTitleWidth(line)))
  );
  const leftOfNodeExtent = maxGroupTitleWidth > 0 ? GROUP_TITLE_GAP + maxGroupTitleWidth : 0;

  // 3b. Assign each node a horizontal "lane" — a persistent x column —
  // instead of independently centering every row. A node that's the
  // *only* child of its parent (the common case: every node inside one
  // node group, chained parent → child → grandchild → ...) inherits its
  // parent's lane exactly, so that whole run sits on one plumb vertical
  // line with a dead-straight connector instead of drifting sideways as
  // row contents change. Only genuine branch points (a parent with
  // several children) and merge points (a node with several parents)
  // claim a fresh lane — and even then, the first child in reading order
  // still inherits the parent's lane, so only the *extra* branches jog.
  const knownParentsOf = new Map<string, string[]>();
  input.forEach((n) => knownParentsOf.set(n.id, n.parents.filter((p) => byId.has(p))));

  const laneOf = new Map<string, number>();
  const laneClaimedFrom = new Set<string>(); // parent ids whose lane a child has already inherited

  columns.forEach((row) => {
    const usedLanesThisRow = new Set<number>();

    // Pass 1: nodes continuing a straight run inherit their (primary)
    // parent's lane, first-come-first-served in row order.
    row.forEach((n) => {
      const primaryParent = knownParentsOf.get(n.id)?.[0];
      if (
          primaryParent &&
          laneOf.has(primaryParent) &&
          !laneClaimedFrom.has(primaryParent) &&
          !usedLanesThisRow.has(laneOf.get(primaryParent)!)
      ) {
        const lane = laneOf.get(primaryParent)!;
        laneOf.set(n.id, lane);
        laneClaimedFrom.add(primaryParent);
        usedLanesThisRow.add(lane);
      }
    });

    // Pass 2: everyone else (roots, extra branches, merges) takes the
    // lowest lane not already claimed in this row. Lanes freed up by
    // chains that ended in an earlier row are fair game — they don't
    // render at the same y, so reusing them can't cause an overlap.
    let nextLane = 0;
    row.forEach((n) => {
      if (laneOf.has(n.id)) return;
      while (usedLanesThisRow.has(nextLane)) nextLane++;
      laneOf.set(n.id, nextLane);
      usedLanesThisRow.add(nextLane);
    });
  });

  // Every lane gets the same fixed-width slot (sized to the widest label
  // in the whole tree), so nodes never collide no matter which lane a
  // later, unrelated row happens to reuse.
  const laneWidth = Math.max(NODE_RADIUS * 2, ...input.map((n) => nodeSize.get(n.id)!.w));
  const lanePitch = laneWidth + NODE_GAP;
  const maxLane = Math.max(...Array.from(laneOf.values()));
  const contentWidth = maxLane * lanePitch + laneWidth;

  // Keep the leftmost lane's circle centered in the canvas — with its
  // group title (if any) hanging off to the left and its own label plus
  // every other lane hanging off to the right — rather than left-aligning
  // the whole diagram. Both sides get the same clearance from their edge
  // to the circle: whichever side actually needs more room (long group
  // title vs. wide content to the right) sets that shared distance, so
  // the shorter side just ends up with extra breathing room instead of
  // dragging the circle off-center.
  const circleToLeftEdge = MARGIN_X + leftOfNodeExtent + NODE_RADIUS;
  const circleToRightEdge = MARGIN_X + Math.max(0, contentWidth - NODE_RADIUS);
  const circleOffset = Math.max(circleToLeftEdge, circleToRightEdge);
  const width = circleOffset * 2;

  const lastRowNodes = columns[maxLevel];
  const lastRowH = lastRowNodes.length > 0 ? Math.max(...lastRowNodes.map((n) => nodeSize.get(n.id)!.h)) : NODE_RADIUS * 2;
  const height = MARGIN_Y * 2 + maxLevel * ROW_SPACING + Math.max(NODE_RADIUS * 2, lastRowH);

  const internalById = new Map<string, InternalNode>();

  columns.forEach((row, lvl) => {
    const y = MARGIN_Y + lvl * ROW_SPACING + NODE_RADIUS;

    row.forEach((n, i) => {
      const { w, h, lines, groupTitleLines } = nodeSize.get(n.id)!;
      const x = circleOffset + laneOf.get(n.id)! * lanePitch;
      internalById.set(n.id, {
        ...n,
        level: lvl,
        order: i,
        x,
        y,
        w,
        h,
        lines,
        groupTitleLines,
        bundleId: null,
      });
    });
  });

  // 4. Bundle links: nodes that share the exact same parent set are routed
  // together and colored together (in bundle mode), producing the braided
  // "tangled" look where strands merge and split between rows.
  const bundleIdFor = new Map<string, string>();
  input.forEach((n) => {
    if (n.parents.length === 0) return;
    const sig = n.parents.filter((p) => byId.has(p)).sort().join("|X|");
    if (!sig) return;
    bundleIdFor.set(n.id, sig);
    const target = internalById.get(n.id);
    if (target) target.bundleId = sig;
  });

  type RawLink = { source: InternalNode; target: InternalNode; bundleId: string };
  const rawLinks: RawLink[] = [];
  input.forEach((n) => {
    const target = internalById.get(n.id);
    if (!target) return;
    n.parents
        .filter((p) => byId.has(p))
        .forEach((p) => {
          const source = internalById.get(p);
          if (!source) return;
          const bundleId = bundleIdFor.get(n.id) ?? `solo-${n.id}-${p}`;
          rawLinks.push({ source, target, bundleId });
        });
  });

  // 5. Distribute multiple lines leaving the same node (or arriving at the
  // same node) evenly along that node's bottom/top edge, using a fixed gap
  // (LINE_GAP) between tracks — like tracks fanning out of a subway
  // station, spaced consistently rather than being squeezed to fit.
  function edgeOffsets(count: number): number[] {
    if (count <= 1) return [0];
    const span = LINE_GAP * (count - 1);
    return Array.from({ length: count }, (_, i) => -span / 2 + i * LINE_GAP);
  }

  const outgoingBySource = new Map<string, RawLink[]>();
  const incomingByTarget = new Map<string, RawLink[]>();
  rawLinks.forEach((l) => {
    if (!outgoingBySource.has(l.source.id)) outgoingBySource.set(l.source.id, []);
    outgoingBySource.get(l.source.id)!.push(l);
    if (!incomingByTarget.has(l.target.id)) incomingByTarget.set(l.target.id, []);
    incomingByTarget.get(l.target.id)!.push(l);
  });

  const sourceOffsetOf = new Map<RawLink, number>();
  outgoingBySource.forEach((group) => {
    const sorted = [...group].sort((a, b) => a.target.x - b.target.x);
    const offsets = edgeOffsets(sorted.length);
    sorted.forEach((l, i) => sourceOffsetOf.set(l, offsets[i]));
  });

  const targetOffsetOf = new Map<RawLink, number>();
  incomingByTarget.forEach((group) => {
    const sorted = [...group].sort((a, b) => a.source.x - b.source.x);
    const offsets = edgeOffsets(sorted.length);
    sorted.forEach((l, i) => targetOffsetOf.set(l, offsets[i]));
  });

  // 6. Every line makes its horizontal run in the gap immediately above its
  // target's row (so a line skipping several generations travels straight
  // down in its own lane and only cuts over right before its child, never
  // slicing through an intervening row). Links landing in the same gap are
  // additionally spread onto distinct y-lanes, fixed LINE_GAP apart, so two
  // horizontal runs that share overlapping x-ranges don't sit on top of
  // each other.
  const byFinalGap = new Map<number, RawLink[]>();
  rawLinks.forEach((l) => {
    const key = l.target.level;
    if (!byFinalGap.has(key)) byFinalGap.set(key, []);
    byFinalGap.get(key)!.push(l);
  });

  const turnYOf = new Map<RawLink, number>();
  byFinalGap.forEach((group) => {
    const target = group[0].target;
    const gapStart = target.y - ROW_SPACING + NODE_RADIUS; // bottom edge of the row directly above target
    const gapEnd = target.y - NODE_RADIUS; // target's own top edge
    const nominalTurnY = gapStart + TURN_FRACTION * (gapEnd - gapStart);

    const sorted = [...group].sort((a, b) => {
      const aMid = (a.source.x + a.target.x) / 2;
      const bMid = (b.source.x + b.target.x) / 2;
      return aMid - bMid;
    });
    const laneOffsets = edgeOffsets(sorted.length);
    const margin = Math.min(CORNER_RADIUS + 2, (gapEnd - gapStart) / 2);

    sorted.forEach((l, i) => {
      const y = nominalTurnY + laneOffsets[i];
      turnYOf.set(l, Math.min(Math.max(y, gapStart + margin), gapEnd - margin));
    });
  });

  const internalLinks: InternalLink[] = rawLinks.map((l) => ({
    source: l.source,
    target: l.target,
    bundleId: l.bundleId,
    sourceOffsetX: sourceOffsetOf.get(l) ?? 0,
    targetOffsetX: targetOffsetOf.get(l) ?? 0,
    turnY: turnYOf.get(l) ?? l.target.y - NODE_RADIUS - TURN_FRACTION * ROW_SPACING,
  }));

  return {
    internalNodes: Array.from(internalById.values()),
    internalLinks,
    width,
    height,
  };
}

// Orthogonal "subway map" connector: leaves the bottom edge of the parent
// dot (at its assigned offset), travels straight down, turns 90 degrees
// onto a single horizontal run, then turns 90 degrees again straight down
// into the top edge of the child dot. Every segment is purely vertical or
// horizontal — only the corners round slightly, the way transit maps do.
//
// The turn (d.turnY) is precomputed in the layout pass: it always sits just
// past halfway through the gap immediately above the target's row, so a
// line skipping several generations travels straight down in its own lane
// the whole way and only cuts over right before reaching its child —
// rather than crossing through the middle of an intervening row. Links
// landing in the same gap are also assigned distinct lanes there, so
// parallel horizontal runs don't overlap.
function tangleLinkPath(d: InternalLink): string {
  const { source, target, sourceOffsetX, targetOffsetX, turnY } = d;
  const x1 = source.x + sourceOffsetX;
  const y1 = source.y + NODE_RADIUS;
  const x2 = target.x + targetOffsetX;
  const y2 = target.y - NODE_RADIUS;

  // Straight line, no turn needed.
  if (Math.abs(x1 - x2) < 0.5) {
    return `M ${x1} ${y1} L ${x2} ${y2}`;
  }

  const r = Math.min(CORNER_RADIUS, Math.abs(turnY - y1), Math.abs(y2 - turnY), Math.abs(x2 - x1) / 2);
  const dir = x2 > x1 ? 1 : -1;
  const vDir = y2 > y1 ? 1 : -1;

  return [
    `M ${x1} ${y1}`,
    `L ${x1} ${turnY - r * vDir}`,
    `Q ${x1} ${turnY} ${x1 + r * dir} ${turnY}`,
    `L ${x2 - r * dir} ${turnY}`,
    `Q ${x2} ${turnY} ${x2} ${turnY + r * vDir}`,
    `L ${x2} ${y2}`,
  ].join(" ");
}